import React, { useState, useEffect } from "react";
import "../App.css";
import {
  COLOR_DARK,
  COLOR_DIVISOR,
  COLOR_LIGHT,
  COLOR_MEDIUM,
  FONT,
  increaseProgress,
  REPEATS,
} from "../defaults";
import {
  InputBoxCheck,
  level0Correct,
  level2Welcome1,
  level2Welcome2,
  level3Incorrect1,
  level5EqualResults,
  level5Numbers,
  level5Welcome,
  nextLevelUnlocked,
  Tutorial0Continue,
} from "../strings";

const LevelNumber = 5;

function calculateMultiplicands(currentProgress) {
  const numbers = [3, 6, 1];
  if (currentProgress >= numbers.length) {
    return Math.ceil(Math.random() * 8) + 1;
  }
  return numbers[currentProgress];
}

function calculateMultipliers(currentProgress) {
  const numbers = [4, 2, 7];
  if (currentProgress >= numbers.length) {
    return Math.ceil(Math.random() * 8) + 1;
  }
  return numbers[currentProgress];
}

function Level5({ levelProgress, setLevelProgress }) {
  const [multiplicand, setMultiplicand] = useState(
    calculateMultiplicands(levelProgress[LevelNumber])
  );
  const [multiplier, setMultiplier] = useState(
    calculateMultipliers(levelProgress[LevelNumber])
  );
  const [message, setMessage] = useState("");
  const [disableInput, setDisableInput] = useState(false);
  const [buttonText, setButtonText] = useState(InputBoxCheck);
  const [checkProgress, setCheckProgress] = useState(0);

  useEffect(() => {
    setMultiplicand(calculateMultiplicands(levelProgress[LevelNumber]));
    setMultiplier(calculateMultipliers(levelProgress[LevelNumber]));
    setInputMultiplier("");
    setInputQuotient("");
  }, [levelProgress]);

  const [inputMultiplier, setInputMultiplier] = useState("");
  const [inputQuotient, setInputQuotient] = useState("");

  const checkAnswers = () => {
    const isMultiplierCorrect = parseInt(inputMultiplier) === multiplier;
    const isQuotientCorrect = parseInt(inputQuotient) === multiplier;
    if (checkProgress === 0) {
      if (isMultiplierCorrect && isQuotientCorrect) {
        if (levelProgress[LevelNumber] + 1 === REPEATS) {
          setMessage(level0Correct + nextLevelUnlocked);
        } else {
          setMessage(level0Correct);
        }
      } else if (inputMultiplier === "" || inputQuotient === "") {
        setMessage(level5Numbers);
      } else if (inputMultiplier !== inputQuotient) {
        setMessage(level5EqualResults);
        if (isMultiplierCorrect) {
          setInputQuotient("");
        } else if (isQuotientCorrect) {
          setInputMultiplier("");
        }
      } else {
        setMessage(level3Incorrect1);
      }
      setDisableInput(true);
      setCheckProgress(1);
      setButtonText(Tutorial0Continue);
    } else {
      if (isMultiplierCorrect && isQuotientCorrect) {
        increaseProgress(levelProgress, setLevelProgress, LevelNumber);
        setInputMultiplier("");
        setInputQuotient("");
      }
      setMessage("");
      setDisableInput(false);
      if (inputMultiplier !== "" && inputQuotient !== "") {
        setInputMultiplier("");
        setInputQuotient("");
      }
      setCheckProgress(0);
      setButtonText(InputBoxCheck);
    }
  };

  const inputStyle = {
    color: "white",
    fontSize: "3dvh",
    textShadow: "0.3svh 0.3svh 0.6svh rgba(0, 0, 0, 0.5)",
    backgroundColor: COLOR_MEDIUM,
    borderRadius: "0.25em",
    boxShadow: "inset 0.3svh 0.3svh 0.6svh rgba(0, 0.5, 0, 0.5)",
    border: "none",
    textAlign: "center",
    width: "5dvh",
    fontFamily: FONT,
  };

  const containerStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textShadow: "0.3svh 0.3svh 0.6svh rgba(0, 0, 0, 0.5)",
    fontFamily: FONT,
    padding: "3dvh",
    borderRadius: "0.25em",
    boxShadow: "0.3svh 0.3svh 0.6svh rgba(0, 0, 0, 0.5)",
    maxWidth: "300px",
    margin: "2dvh auto",
    backgroundColor: COLOR_DARK,
  };

  const rowStyle = {
    display: "flex",
    alignItems: "center",
    marginBottom: "15px",
    fontSize: "3dvh",
  };

  const buttonStyle = {
    padding: "10px 20px",
    fontSize: "3dvh",
    textShadow: "0.3svh 0.3svh 0.6svh rgba(0, 0, 0, 0.5)",
    border: "none",
    cursor: "pointer",
    backgroundColor: COLOR_LIGHT,
    borderRadius: "0.25em",
    boxShadow: "0.3svh 0.3svh 0.6svh rgba(0, 0, 0, 0.5)",
    color: "white",
  };

  const messageStyle = {
    marginTop: "10px",
    fontSize: "3dvh",
    fontWeight: "bold",
  };

  return (
    <div>
      <style>
        {`
          /* Hide arrows in input of type number for WebKit browsers (Chrome, Safari) This was generated by a LLM.*/
          input[type="number"]::-webkit-inner-spin-button,
          input[type="number"]::-webkit-outer-spin-button {
              -webkit-appearance: none;
              margin: 0;
          }

          /* Hide arrows in input of type number for Firefox */
          input[type="number"] {
              -moz-appearance: textfield;
          }
        `}
      </style>
      <div
        className="Welcome-label"
        style={{ fontFamily: FONT, fontSize: "3.5dvh" }}
      >
        {level2Welcome1} {multiplicand * multiplier} {level2Welcome2}{" "}
        <span style={{ color: COLOR_DIVISOR }}>{multiplicand}</span>?<br></br>
        {level5Welcome}
      </div>
      <div style={containerStyle}>
        <div style={rowStyle}>
          <span style={{ color: COLOR_DIVISOR }}>{multiplicand}</span>
          &nbsp;x&nbsp;
          <input
            type="number"
            disabled={disableInput}
            value={inputMultiplier}
            onChange={(e) => setInputMultiplier(e.target.value)}
            style={inputStyle}
          />
          &nbsp;= {multiplicand * multiplier}
        </div>
        <div style={rowStyle}>
          {multiplicand * multiplier} :&nbsp;
          <span style={{ color: COLOR_DIVISOR }}>{multiplicand}</span>
          &nbsp;=&nbsp;
          <input
            type="number"
            disabled={disableInput}
            value={inputQuotient}
            onChange={(e) => setInputQuotient(e.target.value)}
            style={inputStyle}
          />
        </div>
        <button style={buttonStyle} onClick={checkAnswers}>
          {buttonText}
        </button>
        {message && <div style={messageStyle}>{message}</div>}
      </div>
    </div>
  );
}

export default Level5;
